import React, { useEffect, useState } from 'react';
import { Copy, Download, Sparkles, X, Mail } from 'lucide-react';
import { BuilderCard } from '@/components/creation-flow/BuilderCard';
import { cancelRegistration, fetchRegistrations, importAttendance, fetchEmailStats, type Roster, type EmailStats } from '@/services/creatorApi';
import { useToast } from '@/hooks/use-toast';
import { answersToLines } from '@/components/RegistrationQuestionsForm';

const $ = (cents: number) => `$${(cents / 100).toFixed(2)}`;

// Shared purchaser/registrant roster for the builders (featured event / course / video).
// Shows who paid and the creator's share, plus the Hey Joe Plus subscriptions
// this product drove (creator earns a % of each subscriber's first month).
// Self-fetching; renders nothing until the roster loads. Creator-only endpoint.
export default function RosterCard({
  productId,
  noun = 'registered',       // "registered" | "enrolled" | "purchased"
  kicker = 'Roster',          // "Roster" | "Students" | "Buyers"
  fileLabel = 'roster',
  title = '',
}: {
  productId?: string;
  noun?: string;
  kicker?: string;
  fileLabel?: string;
  title?: string;
}) {
  const { toast } = useToast();
  const [roster, setRoster] = useState<Roster | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);   // row awaiting "are you sure"
  const [busyId, setBusyId] = useState<string | null>(null);
  // Attendance: All / Attended / No-show, and the paste box for rooms we can't see into.
  const [attFilter, setAttFilter] = useState<'all' | 'attended' | 'noshow'>('all');
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  // Email: what the reminders, confirmations, follow-ups and any campaign about
  // this event did, and per-row open/click dots. Loaded once, on demand.
  const [emailStats, setEmailStats] = useState<EmailStats | null>(null);
  const [showEmail, setShowEmail] = useState(false);

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    fetchRegistrations(productId).then(r => { if (!cancelled) setRoster(r); }).catch(() => {});
    return () => { cancelled = true; };
  }, [productId]);

  if (!productId || !roster) return null;

  // Cancel a seat (refunding first when it was paid), then reload the roster so
  // the count, earnings and waitlist all reflect the freed seat.
  const doCancel = async (id: string) => {
    setBusyId(id);
    try {
      const r = await cancelRegistration(productId, id);
      toast({ title: r.refunded ? `Refunded ${$(r.refundCents)} and cancelled` : 'Registration cancelled', description: r.buyerEmail ? `${r.buyerEmail} has been emailed.` : undefined });
      setRoster(await fetchRegistrations(productId));
    } catch (e: any) {
      toast({ title: "Couldn't cancel", description: e?.message || 'Try again in a moment.', variant: 'destructive' });
    } finally {
      setBusyId(null); setConfirmId(null);
    }
  };
  const activeRows = roster.registrations.filter(r => !r.cancelled && !r.refunded);
  const openEmail = async () => {
    setShowEmail(v => !v);
    if (!emailStats) { try { setEmailStats(await fetchEmailStats(productId)); } catch { setEmailStats({ flows: [], recipients: {} }); } }
  };
  const pct = (n: number, d: number) => d ? `${Math.round((n / d) * 100)}%` : '–';
  const reconciled = activeRows.filter(r => r.attended != null).length;
  const attendedCount = activeRows.filter(r => r.attended === true).length;
  const visibleRows = roster.registrations.filter(r =>
    attFilter === 'all' ? true : attFilter === 'attended' ? r.attended === true : r.attended === false);

  const downloadCsv = () => {
    const esc = (s: string) => `"${String(s || '').replace(/"/g, '""')}"`;
    // One column per registration question, answers joined with "; " for multi-choice.
    const qs = roster.questions || [];
    const qCols = qs.map(q => esc(q.label)).join(',');
    const qVals = (answers?: Record<string, string | string[]> | null) => qs.map(q => { const v = answers?.[q.id]; return esc(Array.isArray(v) ? v.join('; ') : (v == null ? '' : String(v))); }).join(',');
    // On an agency-only event the people who asked for early access answered the
    // SAME questions, and the host wants one list, not two files to reconcile.
    // A `list` column says which side of the gate each row came from.
    const ea = roster.earlyAccess || [];
    // attended is blank until the occurrence has been reconciled, then yes/no.
    const att = (r: { attended?: boolean | null }) => r.attended == null ? '' : r.attended ? 'yes' : 'no';
    const head = ['list,name,email,event_date,tier,paid,your_share,registered_at,attended,minutes', qCols].filter(Boolean).join(',');
    const regLines = activeRows.map(r =>
      [['registered', esc(r.name), esc(r.email), r.eventDate ? new Date(r.eventDate).toISOString() : '', esc(r.tier || ''), (r.amountPaid / 100).toFixed(2), (r.creatorShareCents / 100).toFixed(2), r.purchasedAt ? new Date(r.purchasedAt).toISOString() : '', att(r), r.attendedMinutes ?? ''].join(','), qs.length ? qVals(r.answers) : ''].filter(Boolean).join(','));
    const eaLines = ea.map(w =>
      [['early-access', esc(w.name), esc(w.email), '', '', '', '', w.joinedAt ? new Date(w.joinedAt).toISOString() : '', '', ''].join(','), qs.length ? qVals(w.answers) : ''].filter(Boolean).join(','));
    const csv = [head, ...regLines, ...eaLines].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `${(title || fileLabel).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${fileLabel}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const btn: React.CSSProperties = { padding: '7px 14px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' };
  const totalEarnings = roster.creatorShareCents + roster.plusCommissionCents;

  return (
    <>
      <BuilderCard kicker={`${kicker} · ${roster.count} ${noun}`}>
        {roster.count === 0 && roster.registrations.length === 0 ? (
          <p className="m-0" style={{ fontSize: 13.5, color: 'var(--hj-beige-ink-3)' }}>No one yet — share your listing link to get the first one.</p>
        ) : (
          <div>
            {/* Earnings summary */}
            <div className="grid grid-cols-3 gap-2" style={{ marginBottom: 12 }}>
              {[
                { label: 'Gross', value: $(roster.grossCents) },
                { label: `Your share (${roster.creatorSharePercent}%)`, value: $(roster.creatorShareCents) },
                { label: 'Total earned', value: $(totalEarnings), accent: true },
              ].map(s => (
                <div key={s.label} className="text-center" style={{ padding: '10px 6px', borderRadius: 'var(--hj-r-sm)', background: '#fff', border: '1px solid var(--hj-beige-line)' }}>
                  <p className="m-0 tabular" style={{ fontSize: 16, fontWeight: 700, color: s.accent ? 'var(--hj-deep-blue)' : 'var(--hj-beige-ink)', fontFamily: 'var(--hj-font-mono)' }}>{s.value}</p>
                  <p className="m-0" style={{ marginTop: 2, fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--hj-beige-ink-3)', fontFamily: 'var(--hj-font-mono)' }}>{s.label}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 12 }}>
              <button type="button" style={btn} className="inline-flex items-center gap-1.5"
                onClick={() => { navigator.clipboard.writeText(activeRows.map(r => r.email).filter(Boolean).join(', ')); toast({ title: `${activeRows.length} emails copied` }); }}>
                <Copy className="w-3.5 h-3.5" /> Copy all emails
              </button>
              <button type="button" style={btn} className="inline-flex items-center gap-1.5" onClick={downloadCsv}>
                <Download className="w-3.5 h-3.5" /> Download CSV
              </button>
              <button type="button" style={{ ...btn, ...(showEmail ? { background: 'var(--hj-beige-ink)', color: '#fff', borderColor: 'var(--hj-beige-ink)' } : {}) }} className="inline-flex items-center gap-1.5" onClick={openEmail}>
                <Mail className="w-3.5 h-3.5" /> Email
              </button>
            </div>
            {showEmail && (
              <div style={{ marginBottom: 12, borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-beige-line)', background: '#fff', overflow: 'hidden' }}>
                {!emailStats ? (
                  <p className="m-0" style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--hj-beige-ink-3)' }}>Loading…</p>
                ) : emailStats.flows.length === 0 ? (
                  <p className="m-0" style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--hj-beige-ink-3)' }}>Nothing sent about this event yet. Reminders and follow-ups show up here once they go out.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: 'var(--hj-beige-hover)' }}>
                        {['Email', 'Sent', 'Delivered', 'Opened', 'Clicked', 'Bounced', 'Unsub'].map(h => (
                          <th key={h} style={{ textAlign: h === 'Email' ? 'left' : 'right', padding: '7px 10px', fontSize: 10.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--hj-beige-ink-3)', fontWeight: 700 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {emailStats.flows.map(f => (
                        <tr key={f.label} style={{ borderTop: '1px solid var(--hj-beige-line)' }}>
                          <td style={{ padding: '7px 10px', color: 'var(--hj-beige-ink)', fontWeight: 600 }}>{f.label}</td>
                          <td className="tabular" style={{ padding: '7px 10px', textAlign: 'right' }}>{f.sent}</td>
                          <td className="tabular" style={{ padding: '7px 10px', textAlign: 'right' }}>{pct(f.delivered, f.sent)}</td>
                          <td className="tabular" style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700 }}>{pct(f.opened, f.sent)}</td>
                          <td className="tabular" style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700 }}>{pct(f.clicked, f.sent)}</td>
                          <td className="tabular" style={{ padding: '7px 10px', textAlign: 'right' }}>{f.bounced}</td>
                          <td className="tabular" style={{ padding: '7px 10px', textAlign: 'right' }}>{f.unsubscribed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            {/* Attendance. Reconciled from the Hey Joe room ~30 min after the stream
                ends; for any other room the host pastes their own list below. */}
            {reconciled > 0 && (
              <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 10 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--hj-beige-ink)' }}>
                  {attendedCount} attended · {reconciled - attendedCount} no-show
                </span>
                {(['all', 'attended', 'noshow'] as const).map(k => (
                  <button key={k} type="button" onClick={() => setAttFilter(k)}
                    style={{ ...btn, padding: '4px 10px', background: attFilter === k ? 'var(--hj-beige-ink)' : 'transparent', color: attFilter === k ? '#fff' : 'var(--hj-beige-ink-2)', borderColor: attFilter === k ? 'var(--hj-beige-ink)' : 'var(--hj-beige-line)' }}>
                    {k === 'all' ? 'All' : k === 'attended' ? 'Attended' : 'No-show'}
                  </button>
                ))}
                <button type="button" style={{ ...btn, padding: '4px 10px' }} className="inline-flex items-center gap-1.5"
                  onClick={() => { const list = activeRows.filter(r => r.attended === false).map(r => r.email).filter(Boolean); navigator.clipboard.writeText(list.join(', ')); toast({ title: `${list.length} no-show emails copied` }); }}>
                  <Copy className="w-3 h-3" /> Copy no-shows
                </button>
              </div>
            )}
            {!roster.attendanceAutomatic && (
              <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 'var(--hj-r-sm)', background: '#fff', border: '1px dashed var(--hj-beige-line)' }}>
                <p className="m-0" style={{ fontSize: 12, color: 'var(--hj-beige-ink-2)', marginBottom: 6 }}>
                  This event isn't in a Hey Joe room, so attendance isn't tracked automatically. Paste the attendee emails from your own report (Zoom, Meet, the door list) and everyone else on the roster is marked a no-show.
                </p>
                <textarea value={importText} onChange={e => setImportText(e.target.value)} rows={3} placeholder="one@example.com, two@example.com"
                  style={{ width: '100%', fontSize: 12.5, fontFamily: 'inherit', padding: '8px 10px', borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-beige-line)', resize: 'vertical' }} />
                <button type="button" disabled={importing || !importText.trim()} style={{ ...btn, marginTop: 6, opacity: importing || !importText.trim() ? 0.6 : 1 }}
                  onClick={async () => {
                    setImporting(true);
                    try {
                      const r = await importAttendance(productId, importText);
                      toast({ title: `${r.attended} attended, ${r.noShow} no-show`, description: r.unmatched.length ? `${r.unmatched.length} pasted address${r.unmatched.length === 1 ? ' is' : 'es are'} not on the roster: ${r.unmatched.slice(0, 3).join(', ')}${r.unmatched.length > 3 ? '…' : ''}` : undefined });
                      setImportText('');
                      setRoster(await fetchRegistrations(productId));
                    } catch (e: any) {
                      toast({ title: "Couldn't import", description: e?.message, variant: 'destructive' });
                    } finally { setImporting(false); }
                  }}>
                  {importing ? 'Marking…' : 'Mark attendance'}
                </button>
              </div>
            )}
            <div className="overflow-y-auto" style={{ maxHeight: 260, borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-beige-line)', background: '#fff' }}>
              {visibleRows.map((r, i) => {
                const gone = !!(r.cancelled || r.refunded);
                const confirming = !!r.id && confirmId === r.id;
                const busy = !!r.id && busyId === r.id;
                return (
                <div key={`${r.email}-${i}`} className="flex items-center justify-between gap-3" style={{ padding: '9px 12px', borderBottom: i < visibleRows.length - 1 ? '1px solid var(--hj-beige-line)' : 'none', opacity: gone ? 0.55 : 1 }}>
                  <div className="flex-1 min-w-0">
                    <p className="truncate m-0" style={{ fontSize: 13, fontWeight: 600, color: 'var(--hj-beige-ink)', textDecoration: gone ? 'line-through' : 'none' }}>
                      {r.name || r.email}
                      {gone && (
                        <span style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', fontSize: 10.5, fontWeight: 700, color: 'var(--hj-beige-ink)', textDecoration: 'none', display: 'inline-block' }}>
                          {r.refunded ? 'refunded' : 'cancelled'}
                        </span>
                      )}
                      {(r as any).tier && (
                        <span style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 999, background: 'var(--hj-beige-hover)', border: '1px solid var(--hj-beige-line)', fontSize: 10.5, fontWeight: 700, color: 'var(--hj-beige-ink)' }}>
                          {(r as any).tier}
                        </span>
                      )}
                      {!gone && r.attended != null && (
                        <span title={r.attendedMinutes != null ? `${r.attendedMinutes} min in the room` : undefined}
                          style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, display: 'inline-block',
                                   background: r.attended ? 'var(--hj-beige-ink)' : '#fff', color: r.attended ? '#fff' : 'var(--hj-beige-ink)', border: '1px solid var(--hj-beige-ink)' }}>
                          {r.attended ? (r.attendedMinutes != null ? `attended · ${r.attendedMinutes}m` : 'attended') : 'no-show'}
                        </span>
                      )}
                    </p>
                    {r.name && <p className="truncate m-0" style={{ fontSize: 11.5, color: 'var(--hj-beige-ink-3)' }}>{r.email}</p>}
                    {showEmail && emailStats && emailStats.recipients[r.email?.toLowerCase()] && (
                      <p className="m-0 truncate" style={{ fontSize: 10.5, color: 'var(--hj-beige-ink-2)', marginTop: 2 }}>
                        {Object.entries(emailStats.recipients[r.email.toLowerCase()]).map(([label, v]) => (
                          <span key={label} style={{ marginRight: 8 }}>
                            <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 999, marginRight: 4, background: v.bounced ? '#B3261E' : v.clicked ? 'var(--hj-beige-ink)' : v.opened ? 'var(--hj-beige-ink-3)' : 'transparent', border: '1px solid var(--hj-beige-ink-3)' }} />
                            {label}{v.clicked ? ' · clicked' : v.opened ? ' · opened' : v.bounced ? ' · bounced' : ''}
                          </span>
                        ))}
                      </p>
                    )}
                    {answersToLines(roster.questions || [], r.answers).map(a => (
                      <p key={a.label} className="m-0" style={{ fontSize: 11.5, color: 'var(--hj-beige-ink)', marginTop: 2 }}><span style={{ color: 'var(--hj-beige-ink-2)' }}>{a.label}:</span> {a.value}</p>
                    ))}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="m-0" style={{ fontSize: 12, fontWeight: 700, color: r.amountPaid > 0 ? 'var(--hj-beige-ink)' : 'var(--hj-beige-ink-3)' }}>
                      {r.amountPaid > 0 ? `${$(r.amountPaid)} · you earn ${$(r.creatorShareCents)}` : 'Free'}
                    </p>
                    {r.eventDate && <p className="m-0" style={{ fontSize: 11, fontWeight: 600, color: 'var(--hj-beige-ink-2)' }}>for {new Date(r.eventDate).toLocaleDateString()}</p>}
                    {r.purchasedAt && <p className="m-0" style={{ fontSize: 10.5, color: 'var(--hj-beige-ink-3)' }}>reg {new Date(r.purchasedAt).toLocaleDateString()}</p>}
                  </div>
                  {/* Cancel (and refund when paid). Two clicks: the second confirms in place. */}
                  {!gone && r.id && (
                    confirming ? (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button type="button" disabled={busy} onClick={() => doCancel(r.id!)}
                          style={{ ...btn, padding: '5px 10px', background: 'var(--hj-beige-ink)', color: '#fff', borderColor: 'var(--hj-beige-ink)', opacity: busy ? 0.6 : 1 }}>
                          {busy ? 'Working…' : r.amountPaid > 0 ? `Refund ${$(r.amountPaid)} & cancel` : 'Yes, cancel'}
                        </button>
                        <button type="button" disabled={busy} onClick={() => setConfirmId(null)} style={{ ...btn, padding: '5px 10px' }}>Keep</button>
                      </div>
                    ) : (
                      <button type="button" title={r.amountPaid > 0 ? 'Refund in full and free the seat' : 'Free the seat'} onClick={() => setConfirmId(r.id!)}
                        className="inline-flex items-center gap-1 flex-shrink-0" style={{ ...btn, padding: '5px 10px' }}>
                        <X className="w-3 h-3" /> Cancel
                      </button>
                    )
                  )}
                </div>
                );
              })}
            </div>
            {/* Waitlist for this occurrence — people who tried after seats filled. */}
            {(roster.waitlistCount ?? 0) > 0 && (
              <div style={{ marginTop: 14 }}>
                <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                  <p className="m-0" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--hj-beige-ink)' }}>Waitlist · {roster.waitlistCount}</p>
                  <button type="button" style={btn} className="inline-flex items-center gap-1.5"
                    onClick={() => { navigator.clipboard.writeText((roster.waitlist || []).map(w => w.email).filter(Boolean).join(', ')); toast({ title: `${roster.waitlistCount} waitlist emails copied` }); }}>
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                </div>
                <div className="overflow-y-auto" style={{ maxHeight: 170, borderRadius: 'var(--hj-r-sm)', border: '1px dashed var(--hj-beige-line)', background: '#fff' }}>
                  {(roster.waitlist || []).map((w, i) => (
                    <div key={`wl-${w.email}-${i}`} className="flex items-center justify-between" style={{ padding: '8px 12px', borderBottom: i < (roster.waitlist!.length - 1) ? '1px solid var(--hj-beige-line)' : 'none' }}>
                      <span style={{ fontSize: 12.5, color: 'var(--hj-beige-ink)' }}>{w.name || w.email}</span>
                      <span style={{ fontSize: 11, color: 'var(--hj-beige-ink-3)' }}>{w.joinedAt ? new Date(w.joinedAt).toLocaleDateString() : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Early access on an agency-only event: the people outside the roster who
                raised a hand, and what they answered. */}
            {(roster.earlyAccess || []).length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                  <p className="m-0" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--hj-beige-ink)' }}>Early access requests · {roster.earlyAccess!.length}</p>
                  <div className="flex gap-1.5">
                    <button type="button" style={btn} className="inline-flex items-center gap-1.5"
                      onClick={() => { navigator.clipboard.writeText(roster.earlyAccess!.map(w => w.email).filter(Boolean).join(', ')); toast({ title: `${roster.earlyAccess!.length} emails copied` }); }}>
                      <Copy className="w-3.5 h-3.5" /> Copy
                    </button>
                    <button type="button" style={btn} className="inline-flex items-center gap-1.5"
                      onClick={() => {
                        const esc = (s: string) => `"${String(s || '').replace(/"/g, '""')}"`;
                        const qs = roster.questions || [];
                        const rows = ['name,email,requested_at,' + qs.map(q => esc(q.label)).join(','), ...roster.earlyAccess!.map(w => [esc(w.name), esc(w.email), w.joinedAt ? new Date(w.joinedAt).toISOString() : '', ...qs.map(q => { const v = w.answers?.[q.id]; return esc(Array.isArray(v) ? v.join('; ') : (v == null ? '' : String(v))); })].join(','))];
                        const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([rows.join('\n')], { type: 'text/csv' })); a.download = 'early-access.csv'; a.click();
                      }}>
                      <Download className="w-3.5 h-3.5" /> CSV
                    </button>
                  </div>
                </div>
                <div className="overflow-y-auto" style={{ maxHeight: 260, borderRadius: 'var(--hj-r-sm)', border: '1px dashed var(--hj-beige-line)', background: '#fff' }}>
                  {roster.earlyAccess!.map((w, i) => (
                    <div key={`ea-${w.email}-${i}`} style={{ padding: '8px 12px', borderBottom: i < roster.earlyAccess!.length - 1 ? '1px solid var(--hj-beige-line)' : 'none' }}>
                      <div className="flex items-center justify-between gap-3">
                        <span className="truncate" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--hj-beige-ink)' }}>{w.name || w.email}</span>
                        <span style={{ fontSize: 11, color: 'var(--hj-beige-ink-3)', flexShrink: 0 }}>{w.joinedAt ? new Date(w.joinedAt).toLocaleDateString() : ''}</span>
                      </div>
                      {w.name && <p className="truncate m-0" style={{ fontSize: 11.5, color: 'var(--hj-beige-ink-3)' }}>{w.email}</p>}
                      {answersToLines(roster.questions || [], w.answers).map(a => (
                        <p key={a.label} className="m-0" style={{ fontSize: 11.5, color: 'var(--hj-beige-ink)', marginTop: 2 }}><span style={{ color: 'var(--hj-beige-ink-2)' }}>{a.label}:</span> {a.value}</p>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </BuilderCard>

      {/* Plus subscriptions this product drove — commission on first month. */}
      {roster.plusSignups.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <BuilderCard kicker={`Plus signups you drove · ${roster.plusSignups.length}`}>
            <p className="m-0 flex items-start gap-1.5" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)', marginBottom: 12, lineHeight: 1.5 }}>
              {/* Brand yellow is invisible on the beige card — use paper ink. */}
              <Sparkles className="w-3.5 h-3.5 flex-shrink-0" style={{ marginTop: 2, color: 'var(--hj-beige-ink)' }} />
              <span>These people subscribed to Hey Joe Plus through your page's join link and {noun} — you earn <strong>{roster.plusCommissionPercent}% of their first month</strong>. Earned so far: <strong>{$(roster.plusCommissionCents)}</strong>.</span>
            </p>
            <div className="overflow-y-auto" style={{ maxHeight: 220, borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-beige-line)', background: '#fff' }}>
              {roster.plusSignups.map((s, i) => (
                <div key={`${s.email}-${i}`} className="flex items-center justify-between gap-3" style={{ padding: '9px 12px', borderBottom: i < roster.plusSignups.length - 1 ? '1px solid var(--hj-beige-line)' : 'none' }}>
                  <div className="flex-1 min-w-0">
                    <p className="truncate m-0" style={{ fontSize: 13, fontWeight: 600, color: 'var(--hj-beige-ink)' }}>{s.name || s.email}</p>
                    <p className="truncate m-0" style={{ fontSize: 11.5, color: 'var(--hj-beige-ink-3)' }}>{s.name ? `${s.email} · ` : ''}{s.tierName}{s.subscribedAt ? ` · ${new Date(s.subscribedAt).toLocaleDateString()}` : ''}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="m-0" style={{ fontSize: 12, fontWeight: 700, color: s.pending ? 'var(--hj-beige-ink-3)' : 'var(--hj-deep-blue)' }}>
                      {s.pending ? `trial · ${$(s.commissionCents)} pending` : `+${$(s.commissionCents)}`}
                    </p>
                    {!s.pending && s.payout && (
                      <p className="m-0" style={{ fontSize: 10.5, color: s.payout === 'paid' ? 'var(--hj-beige-ink-3)' : 'var(--hj-beige-ink-2)' }}>
                        {s.payout === 'paid' ? 'paid out' : s.payout === 'awaiting_event' ? 'pays after the event' : s.payout === 'void' ? 'no payout, membership ended' : 'payout queued'}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </BuilderCard>
        </div>
      )}
    </>
  );
}
